import { apiClient } from '../config/api';
import { toDate } from '../utils/apiDate';
import {
  Product,
  ProductCategory,
  Order,
  CartItem,
  MarketplaceFilters,
  SearchResult,
  SellerProfile,
} from '../types/marketplace';

export interface ReceivingEntityOption {
  id: string;
  type: 'personal' | 'entity';
  name: string;
  stripeConnectAccountId: string;
  entityId: string | null;
  memberId: string;
  isPersonal: boolean;
  status: string;
  paymentSetupComplete: boolean;
}

class MarketplaceService {
  private apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';

  /**
   * Check if marketplace is enabled
   */
  async getMarketplaceStatus(): Promise<{ isEnabled: boolean; disabledReason?: string }> {
    try {
      const response = await apiClient.get('/api/marketplace/status');

      if (!response.ok) {
        // If the endpoint fails, assume marketplace is disabled
        return { isEnabled: false, disabledReason: 'Service unavailable' };
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error checking marketplace status:', error);
      // If there's an error, assume marketplace is disabled for safety
      return { isEnabled: false, disabledReason: 'Service unavailable' };
    }
  }

  /**
   * Search products with filters
   */
  async searchProducts(
    filters?: MarketplaceFilters,
    page: number = 1,
    limit: number = 20,
  ): Promise<SearchResult> {
    try {
      const params = new URLSearchParams();

      if (filters?.searchQuery) {
        params.append('q', filters.searchQuery);
      }
      if (filters?.category) {
        params.append('category', filters.category);
      }
      if (filters?.entityId) {
        params.append('entityId', filters.entityId);
      }
      if (filters?.priceRange) {
        params.append('minPrice', filters.priceRange.min.toString());
        params.append('maxPrice', filters.priceRange.max.toString());
      }
      if (filters?.sortBy) {
        params.append('sortBy', filters.sortBy);
      }
      if (filters?.localPickup !== undefined) {
        params.append('localPickup', filters.localPickup ? 'true' : 'false');
        if (filters.localPickup) {
          if (filters.pickupZipCode) {
            params.append('pickupZipCode', filters.pickupZipCode);
          }
          if (filters.pickupRadiusMiles !== undefined) {
            params.append('pickupRadiusMiles', filters.pickupRadiusMiles.toString());
          }
        }
      }

      params.append('page', page.toString());
      params.append('limit', limit.toString());

      const response = await apiClient.get(`/api/marketplace/products/search?${params.toString()}`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to search products');
      }

      const data = await response.json();
      const rawProducts = data.data || data.products || [];
      const products = Array.isArray(rawProducts) ? rawProducts : [];
      const pagination = data.pagination || {};

      return {
        products: products.map(this.transformProduct),
        totalCount: pagination.total ?? data.totalCount ?? 0,
        hasMore: pagination.hasMore ?? data.hasMore ?? false,
      };
    } catch (error) {
      console.error('Error searching products:', error);
      throw error;
    }
  }

  /**
   * Get product by ID
   */
  async getProduct(productId: string): Promise<Product> {
    try {
      const response = await apiClient.get(`/api/marketplace/products/${productId}`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch product');
      }

      const data = await response.json();
      const product = data.data || data;
      return this.transformProduct(product);
    } catch (error) {
      console.error('Error fetching product:', error);
      throw error;
    }
  }

  /**
   * Get favorite status for a product
   */
  async getFavoriteStatus(productId: string): Promise<boolean> {
    try {
      const response = await apiClient.get(`/api/marketplace/products/${productId}/favorite`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch favorite status');
      }

      const data = await response.json();
      return data?.data?.isFavorite ?? false;
    } catch (error) {
      console.error('Error fetching favorite status:', error);
      return false;
    }
  }

  /**
   * Toggle favorite for a product
   */
  async toggleFavorite(productId: string): Promise<boolean> {
    try {
      const response = await apiClient.post(`/api/marketplace/products/${productId}/favorite`, {});

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to toggle favorite');
      }

      const data = await response.json();
      return data?.data?.isFavorite ?? false;
    } catch (error) {
      console.error('Error toggling favorite:', error);
      throw error;
    }
  }

  /**
   * Get all product categories
   */
  async getCategories(): Promise<ProductCategory[]> {
    try {
      const response = await apiClient.get('/api/marketplace/categories');

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch categories');
      }

      const data = await response.json();
      const rawCategories = data.data || data || [];
      const categories = Array.isArray(rawCategories) ? rawCategories : [];
      return categories.map(this.transformCategory);
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  /**
   * Create a new product (seller only)
   */
  async createProduct(
    productData: Partial<Product> & { primaryEntityId?: string; hierarchicalPosting?: any },
  ): Promise<Product> {
    try {
      const response = await apiClient.post('/api/marketplace/products', productData);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create product');
      }

      const data = await response.json();
      const product = data.data || data;
      return this.transformProduct(product);
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  }

  /**
   * Update product (seller only)
   */
  async updateProduct(productId: string, updates: Partial<Product>): Promise<Product> {
    try {
      const response = await apiClient.patch(`/api/marketplace/products/${productId}`, updates);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update product');
      }

      const data = await response.json();
      const product = data.data || data;
      return this.transformProduct(product);
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  }

  /**
   * Delete product (seller only)
   */
  async deleteProduct(productId: string): Promise<void> {
    try {
      const response = await apiClient.delete(`/api/marketplace/products/${productId}`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete product');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  }

  /**
   * Get seller's products
   */
  async getSellerProducts(page: number = 1, limit: number = 20): Promise<SearchResult> {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());

      const response = await apiClient.get(`/api/marketplace/seller/products?${params.toString()}`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch seller products');
      }

      const data = await response.json();
      const rawProducts = data.data || data.products || [];
      const products = Array.isArray(rawProducts) ? rawProducts : [];
      const pagination = data.pagination || {};

      return {
        products: products.map(this.transformProduct),
        totalCount: pagination.total ?? data.totalCount ?? 0,
        hasMore: pagination.hasMore ?? data.hasMore ?? false,
      };
    } catch (error) {
      console.error('Error fetching seller products:', error);
      throw error;
    }
  }

  /**
   * Get available receiving entities (Stripe Connect accounts) for the current user
   */
  async getReceivingEntities(): Promise<ReceivingEntityOption[]> {
    try {
      const response = await apiClient.get('/api/marketplace/receiving-entities');

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch payment accounts');
      }

      const data = await response.json();
      const rawEntities = data.data || data || [];
      return Array.isArray(rawEntities) ? rawEntities : [];
    } catch (error) {
      console.error('Error fetching receiving entities:', error);
      throw error;
    }
  }

  /**
   * Get seller profile
   */
  async getSellerProfile(): Promise<SellerProfile | null> {
    try {
      const response = await apiClient.get('/api/marketplace/seller/profile');

      if (response.status === 404) {
        return null; // No seller profile exists
      }

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch seller profile');
      }

      const data = await response.json();
      const profile = data.data || data;
      return this.transformSellerProfile(profile);
    } catch (error) {
      console.error('Error fetching seller profile:', error);
      throw error;
    }
  }

  /**
   * Create seller profile
   */
  async createSellerProfile(): Promise<SellerProfile> {
    try {
      const response = await apiClient.post('/api/marketplace/seller/profile', {});

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create seller profile');
      }

      const data = await response.json();
      const profile = data.data || data;
      return this.transformSellerProfile(profile);
    } catch (error) {
      console.error('Error creating seller profile:', error);
      throw error;
    }
  }

  /**
   * Get Stripe Connect onboarding URL
   */
  async getStripeConnectUrl(): Promise<string> {
    try {
      const response = await apiClient.post('/api/marketplace/seller/stripe-connect', {});

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to get Stripe Connect URL');
      }

      const data = await response.json();
      return data.data?.onboardingUrl || data.onboardingUrl || data.url;
    } catch (error) {
      console.error('Error getting Stripe Connect URL:', error);
      throw error;
    }
  }

  /**
   * Refresh Stripe Connect onboarding status after redirect
   */
  async refreshStripeConnectStatus(accountId?: string): Promise<SellerProfile | null> {
    try {
      if (!accountId) {
        return await this.getSellerProfile();
      }

      const query = `?account_id=${encodeURIComponent(accountId)}`;
      const response = await apiClient.get(
        `/api/marketplace/seller/stripe-connect/callback${query}`,
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to refresh Stripe Connect status');
      }

      await response.json();
      return await this.getSellerProfile();
    } catch (error) {
      console.error('Error refreshing Stripe Connect status:', error);
      throw error;
    }
  }

  /**
   * Update seller profile
   */
  async updateSellerProfile(updates: Partial<SellerProfile>): Promise<SellerProfile> {
    try {
      const response = await apiClient.patch('/api/marketplace/seller/profile', updates);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update seller profile');
      }

      const data = await response.json();
      const profile = data.data || data;
      return this.transformSellerProfile(profile);
    } catch (error) {
      console.error('Error updating seller profile:', error);
      throw error;
    }
  }

  /**
   * Get buyer's orders
   */
  async getBuyerOrders(
    page: number = 1,
    limit: number = 20,
  ): Promise<{ orders: Order[]; hasMore: boolean }> {
    try {
      // The API paginates by offset; sending `page` left every request on the
      // first page, so "next page" silently re-fetched the same orders.
      const params = new URLSearchParams();
      params.append('role', 'buyer');
      params.append('limit', limit.toString());
      params.append('offset', String(Math.max(0, (page - 1) * limit)));

      const response = await apiClient.get(`/api/marketplace/orders?${params.toString()}`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch orders');
      }

      const data = await response.json();
      const rawOrders = data.data || data.orders || [];
      const orders = Array.isArray(rawOrders) ? rawOrders : [];
      const pagination = data.pagination || {};

      return {
        orders: orders.map(this.transformOrder),
        hasMore: pagination.hasMore ?? data.hasMore ?? false,
      };
    } catch (error) {
      console.error('Error fetching buyer orders:', error);
      throw error;
    }
  }

  /**
   * Get seller's orders
   */
  async getSellerOrders(
    page: number = 1,
    limit: number = 20,
  ): Promise<{ orders: Order[]; hasMore: boolean }> {
    try {
      // /marketplace/seller/orders is a placeholder that always returns an
      // empty list; the orders endpoint serves either role for real.
      const params = new URLSearchParams();
      params.append('role', 'seller');
      params.append('limit', limit.toString());
      params.append('offset', String(Math.max(0, (page - 1) * limit)));

      const response = await apiClient.get(`/api/marketplace/orders?${params.toString()}`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch seller orders');
      }

      const data = await response.json();
      const rawOrders = data.data || data.orders || [];
      const orders = Array.isArray(rawOrders) ? rawOrders : [];
      const pagination = data.pagination || {};

      return {
        orders: orders.map(this.transformOrder),
        hasMore: pagination.hasMore ?? data.hasMore ?? false,
      };
    } catch (error) {
      console.error('Error fetching seller orders:', error);
      throw error;
    }
  }

  /**
   * Update order fulfillment (seller only)
   */
  async updateOrderFulfillment(
    orderId: string,
    fulfillmentData: {
      trackingNumber: string;
      carrier: string;
    },
  ): Promise<Order> {
    try {
      const response = await apiClient.patch(
        `/api/marketplace/seller/orders/${orderId}/fulfill`,
        fulfillmentData,
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update order fulfillment');
      }

      const data = await response.json();
      return this.transformOrder(data);
    } catch (error) {
      console.error('Error updating order fulfillment:', error);
      throw error;
    }
  }

  /**
   * Create order (checkout)
   */
  async createOrder(orderData: {
    items: CartItem[];
    shippingAddress: any;
    paymentMethodId: string;
  }): Promise<Order> {
    try {
      const response = await apiClient.post('/api/marketplace/orders', orderData);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create order');
      }

      const data = await response.json();
      const orderDataRaw = data.data?.order || data.order || data.data || data;
      return this.transformOrder(orderDataRaw);
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  }

  /**
   * Create marketplace order and return payment intent for checkout
   */
  async createMarketplaceOrder(orderData: {
    items: Array<{ productId: string; variantId: string; quantity: number }>;
    shippingAddress: any;
    paymentMethodId: string;
  }): Promise<{ order: Order; paymentIntent: { id: string; clientSecret: string } }> {
    try {
      const response = await apiClient.post('/api/marketplace/orders', orderData);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create order');
      }

      const data = await response.json();
      const orderRaw = data.data?.order || data.order || data.data || data;
      const paymentIntentRaw = data.data?.paymentIntent || data.paymentIntent;

      if (!paymentIntentRaw?.id || !paymentIntentRaw?.clientSecret) {
        throw new Error('Payment intent not available');
      }

      return {
        order: this.transformOrder(orderRaw),
        paymentIntent: {
          id: paymentIntentRaw.id,
          clientSecret: paymentIntentRaw.clientSecret,
        },
      };
    } catch (error) {
      console.error('Error creating marketplace order:', error);
      throw error;
    }
  }

  /**
   * Confirm marketplace order payment
   */
  async confirmMarketplacePayment(paymentIntentId: string, paymentMethodId?: string) {
    try {
      const response = await apiClient.post('/api/marketplace/orders/confirm-payment', {
        paymentIntentId,
        paymentMethodId,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to confirm marketplace payment');
      }

      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error('Error confirming marketplace payment:', error);
      throw error;
    }
  }

  /**
   * Get order by ID
   */
  async getOrder(orderId: string): Promise<Order> {
    try {
      const response = await apiClient.get(`/api/marketplace/orders/${orderId}`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch order');
      }

      const data = await response.json();
      const orderRaw = data.data || data.order || data;
      return this.transformOrder(orderRaw);
    } catch (error) {
      console.error('Error fetching order:', error);
      throw error;
    }
  }

  /**
   * Upload product image
   */
  async uploadProductImage(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${this.apiBaseUrl}/api/marketplace/products/images`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getAuthToken()}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to upload image');
      }

      const data = await response.json();
      return data.imageUrl;
    } catch (error) {
      console.error('Error uploading product image:', error);
      throw error;
    }
  }

  /**
   * Get authentication token
   */
  private async getAuthToken(): Promise<string | null> {
    const { getCurrentUserToken } = await import('../config/api');
    return getCurrentUserToken();
  }

  /**
   * Transform API product data to client format
   */
  private transformProduct(data: any): Product {
    return {
      ...data,
      createdAt: new Date(data.createdAt),
      updatedAt: new Date(data.updatedAt),
    };
  }

  /**
   * Transform API category data to client format
   */
  private transformCategory(data: any): ProductCategory {
    return {
      ...data,
      createdAt: new Date(data.createdAt),
      updatedAt: new Date(data.updatedAt),
    };
  }

  /**
   * Transform API order data to client format
   */
  private transformOrder(data: any): Order {
    return {
      ...data,
      createdAt: toDate(data.createdAt) || new Date(0),
      updatedAt: toDate(data.updatedAt) || new Date(0),
      fulfillment: data.fulfillment
        ? {
            ...data.fulfillment,
            shippedAt: toDate(data.fulfillment.shippedAt) || new Date(0),
            deliveredAt: toDate(data.fulfillment.deliveredAt) || undefined,
          }
        : undefined,
    };
  }

  /**
   * Transform API seller profile data to client format
   */
  private transformSellerProfile(data: any): SellerProfile {
    return {
      ...data,
      createdAt: new Date(data.createdAt),
      updatedAt: new Date(data.updatedAt),
      suspendedAt: data.suspendedAt ? new Date(data.suspendedAt) : undefined,
    };
  }
}

export const marketplaceService = new MarketplaceService();
export default marketplaceService;
